import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NestedFormGroupType } from '../../../../../../../../../core/models/form-group-type';
import { AttachmentFormModel } from '../../../../../models/attachment/attachment-form-model';
import { v4 as uuidv4 } from 'uuid';
import { RegExPattern } from '../../../../../../../../../core/models/regexp-pattern/regex-pattern';
import { AttachmentConstants } from '../../../../../constants/attachment-constants';
import { AttachmentUpdateRequest } from '../../../../../models/attachment/attachment-update-request';
import { AttachmentCreationRequest } from '../../../../../models/attachment/attachment-creation-request';

@Injectable({
    providedIn: 'root',
})
export class AttachmentFormService {
    getFormDefinition(): FormGroup<NestedFormGroupType<AttachmentFormModel>> {
        return new FormGroup<NestedFormGroupType<AttachmentFormModel>>({
            guid: new FormControl<string>(uuidv4()),
            id: new FormControl<string | null>(null),
            filename: new FormControl<string | null>(null),
            file: new FormControl<File | null>(null),
            attachmentTypeCode: new FormControl<string | null>(null, {
                validators: [Validators.required],
            }),
            description: new FormControl<string | null>(null, [
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
                Validators.maxLength(
                    AttachmentConstants.MAXIMUM_DESCRIPTION_LENGTH,
                ),
            ]),
            isDeleted: new FormControl<boolean>(false),
        });
    }

    /**
     * Determines if the attachment is new (i.e., has no ID and has a file).
     */
    isNewAttachment(
        attachmentForm: FormGroup<NestedFormGroupType<AttachmentFormModel>>,
    ): boolean {
        return (
            attachmentForm.value.id === null &&
            attachmentForm.value.file !== null
        );
    }

    /**
     * Determines if the attachment is an existing (i.e., has an ID and no file).
     */
    isExistingAttachment(
        attachmentForm: FormGroup<NestedFormGroupType<AttachmentFormModel>>,
    ): boolean {
        return (
            attachmentForm.value.id !== null &&
            attachmentForm.value.file === null
        );
    }

    /**
     * Determines if the attachment is marked for deletion.
     */
    isDeletedAttachment(
        attachmentForm: FormGroup<NestedFormGroupType<AttachmentFormModel>>,
    ): boolean {
        return attachmentForm.value.isDeleted === true;
    }

    generateCreationRequestObject(
        attachmentForm: FormGroup<NestedFormGroupType<AttachmentFormModel>>,
    ): AttachmentCreationRequest {
        return this.generateRequestObject<AttachmentCreationRequest>(
            attachmentForm,
        );
    }

    generateUpdateRequestObject(
        attachmentForm: FormGroup<NestedFormGroupType<AttachmentFormModel>>,
    ): AttachmentUpdateRequest {
        return this.generateRequestObject<AttachmentUpdateRequest>(
            attachmentForm,
        );
    }

    private generateRequestObject<
        T extends AttachmentCreationRequest | AttachmentUpdateRequest,
    >(attachmentForm: FormGroup<NestedFormGroupType<AttachmentFormModel>>): T {
        const formValue = attachmentForm.value as Required<T>;

        return {
            ...formValue,
        } as T;
    }
}
